import { useCallback, useState } from 'react'
import { supabase } from '../lib/supabase'
import type { CPModelEstimate, CriticalPowerPoint, WBalPoint } from './usePowerStats'

export interface AnalysisResult {
  grade: 'excellent' | 'partially' | 'poor' | 'not-graded'
  percentage: number
  insights: {
    category?: 'intervals' | 'laps' | 'metrics' | 'heart_rate_zones'
    level: 'significant' | 'moderate'
    description: string
  }[]
  totalWeights: number
  criticalPower?: CriticalPowerPoint[] | null
  cpModel?: CPModelEstimate | null
  wBal?: WBalPoint[] | null
  cpReference?: CPReference | null
}

export interface CPReference {
  dateRange: string[]
  values: CriticalPowerPoint[]
}

export interface UseWorkoutAnalysisReturn {
  analysisResult: AnalysisResult | null
  isLoading: boolean
  error: string | null
  analyseWorkout: (workoutId: string) => Promise<void>
  reset: () => void
}

/**
 * Hook for analyzing workout execution using the analyse-workout edge function
 * Provides execution grade and insights based on workout data
 */
export const useWorkoutAnalysis = (): UseWorkoutAnalysisReturn => {
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const analyseWorkout = useCallback(async (workoutId: string) => {
    if (!workoutId) {
      setError('No workout ID provided')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (!session) {
        setError('Not authenticated')
        setIsLoading(false)
        return
      }

      const { data, error } = await supabase.functions.invoke('analyse-workout', {
        body: {
          workout_id: workoutId,
        },
      })
      if (error) {
        console.error('Error invoking analyse-workout edge function:', error)
        throw new Error(`Failed to perform workout analysis`)
      }

      setAnalysisResult({
        grade: data.grade,
        percentage: data.percentage || 0,
        insights: data.insights || [],
        totalWeights: data.totalWeights || 0,
        criticalPower: data.criticalPower || null,
        cpModel: data.cpModel || null,
        wBal: data.wBal || null,
        cpReference: data.cpReference || null,
      })
    } catch (err) {
      console.error('Error analysing workout:', err)
      setError(err instanceof Error ? err.message : 'Failed to analyse workout')
    } finally {
      setIsLoading(false)
    }
  }, [])

  const reset = useCallback(() => {
    setAnalysisResult(null)
    setError(null)
    setIsLoading(false)
  }, [])

  return {
    analysisResult,
    isLoading,
    error,
    analyseWorkout,
    reset,
  }
}
